import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {DomSanitizer, SafeHtml, SafeResourceUrl} from '@angular/platform-browser';
import {PageHtml5DTO} from '@tj/tj-dmp-lib-api';
import { environment } from '../../../../environments/environment';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'dmp-iframe',
    templateUrl: './iframe.component.html',
    styleUrls: ['./iframe.component.scss'],
    standalone: false
})
export class IframeComponent implements OnChanges, OnInit {
    @Input() contentHtml: string;
    @Input() src: string;
    @Input() pageHtml5: PageHtml5DTO;
    @Input() width: string;
    @Input() height = '800px';
    @Input() displayDevice: string;
    @Input() datastore: string;

    urlPreview: SafeResourceUrl;
    htmlContent: string;
    sanitizedHtmlContent: SafeHtml; 

    private readonly key = 'previewDisplayDevice';

    constructor(
        protected sanitizer: DomSanitizer
    ) {
        const value = localStorage.getItem(this.key);
        if (value == null) {
            this.savedDisplayDevice('desktop');
        } else {
            this.displayDevice = value;
        }
    }

    private savedDisplayDevice(displayDevice: string = null) {
        if (displayDevice) {
            this.displayDevice = displayDevice;
        }
        localStorage.setItem(this.key, this.displayDevice);
    }

    ngOnInit(): void {
        this.changeScreen();
        this.prepareContent();
    }

    private initPageHtml5() {
        if (this.pageHtml5 && this.datastore && environment.landingPagePreviewBaseUrl) {
            const encodedCode = encodeURIComponent(this.pageHtml5.code);
            const src = `${environment.landingPagePreviewBaseUrl}/${this.datastore}/TEST?pageHtml=${encodedCode}&lang=FR`;
            this.urlPreview = this.sanitizer.bypassSecurityTrustResourceUrl(src);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.contentHtml || changes.src || changes.pageHtml5) {
            this.prepareContent();
        }
        if (changes.displayDevice) {
            this.changeScreen();
        }
    }

    private prepareContent() {
        if (this.src) {
            // Utiliser une URL externe
            this.urlPreview = this.sanitizer.bypassSecurityTrustResourceUrl(this.src);
            this.htmlContent = null;
            this.sanitizedHtmlContent = null; 
        } else if (this.pageHtml5) {
            // Générer l'URL de prévisualisation
            this.initPageHtml5();
            this.htmlContent = null;
            this.sanitizedHtmlContent = null; 
        } else if (this.contentHtml) {
            // Utiliser le contenu HTML direct via srcdoc
            this.urlPreview = null;
            this.htmlContent = this.contentHtml;
            const cleanedHtml = this.sanitizeEmailHtml(this.contentHtml);
            this.sanitizedHtmlContent = this.sanitizer.bypassSecurityTrustHtml(cleanedHtml);
        }
    }

    private sanitizeEmailHtml(html: string): string {
        // 1. Remove <script> tags
        html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

        // 2. Remove ALL on* event handlers (onclick, onerror, onload, etc.)
        html = html.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
        html = html.replace(/\son\w+\s*=\s*[^\s>]*/gi, '');

        // 3. Remove javascript: protocol in all attributes
        html = html.replace(/javascript:/gi, '');

        // 4. Remove dangerous data: URLs (except images)
        html = html.replace(/src\s*=\s*["']data:(?!image)/gi, 'src="');
        html = html.replace(/href\s*=\s*["']data:/gi, 'href="');
        html = html.replace(/srcdoc\s*=\s*["'][^"']*["']/gi, '');

        // 5. Remove dangerous tags
        const dangerousTags = ['iframe', 'object', 'embed', 'applet', 'link', 'style', 'base', 'meta', 'form'];
        dangerousTags.forEach(tag => {
            const regex = new RegExp(`<${tag}\\b[^<]*(?:(?!<\/${tag}>)<[^<]*)*<\/${tag}>`, 'gi');
            html = html.replace(regex, '');
            html = html.replace(new RegExp(`<${tag}[^>]*>`, 'gi'), '');
        });

        // 6. Neutralize anchor links to prevent navigation hijacking
        html = html.replace(/href\s*=\s*["']#["']/gi, 'href="javascript:void(0)"');

        return html;
    }

    changeScreen(
        displayDevice: string = this.displayDevice,
        event: Event = null
    ) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.displayDevice = displayDevice;
        switch (displayDevice) {
            case 'mobile':
                this.width = '320px';
                break;
            case 'tablette':
                this.width = '480px';
                break;
            case 'desktop':
            default:
                this.width = '100%';
                break;
        }
    }

    isTablette() {
        return this.displayDevice === 'tablette';
    }

    isMobile() {
        return this.displayDevice === 'mobile';
    }

    isDesktop() {
        return this.displayDevice === 'desktop' || this.displayDevice == null;
    }
}
